import { useEffect, useState } from "react";
import {
  Plus,
  X,
  Edit3,
  Trash2,
  Search,
  Upload,
  Download,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
} from "lucide-react";
import Swal from "sweetalert2";
import API_BASE_URL from "../config";

export default function ExpenseTypePage({ setCurrentPage }) {
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [filtered, setFiltered] = useState([]);

  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);

  const [formData, setFormData] = useState({
    expenseTypeCode: "",
    expenseTypeName: "",
    isActive: true,
    leadTime: "",
  });

  const [search, setSearch] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const perPage = 8;
  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  // ---------------- API FUNCTIONS ---------------- //

  const fetchExpenseTypes = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/ExpenseType`);
      if (!res.ok) throw new Error("Failed to fetch expense types");

      const data = await res.json();

      // Map to handle both camelCase and PascalCase
      const mapped = data.map((e) => ({
        id: e.id,
        expenseTypeCode: e.expenseTypeCode || e.ExpenseTypeCode,
        expenseTypeName: e.expenseTypeName || e.ExpenseTypeName,
        isActive: e.isActive === 1 || e.isActive === true || e.IsActive === true,
        leadTime: e.leadTime || e.LeadTime,
        createdAt: e.createdAt || e.created_at,
      }));

      setExpenseTypes(mapped);
      setFiltered(mapped);
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to fetch expense types", "error");
    }
  };
const createExpense = async () => {
  if (!formData.expenseTypeName) {
    Swal.fire("Oops!", "Expense Type Name is required", "warning");
    return;
  }

  try {
    const fd = new FormData();
    fd.append("ExpenseTypeCode", formData.expenseTypeCode);
    fd.append("ExpenseTypeName", formData.expenseTypeName);
    fd.append("LeadTime", formData.leadTime || "");
    fd.append("IsActive", formData.isActive ? 1 : 0);

    const res = await fetch(`${API_BASE_URL}api/ExpenseType`, {
      method: "POST",
      body: fd, // ✅ FormData, no Content-Type
    });

    if (!res.ok) throw new Error("Failed to create expense type");

    setOpenCreate(false);
    clearForm();
    fetchExpenseTypes();
    Swal.fire("Success!", "Expense Type created successfully", "success");
  } catch (err) {
    console.error(err);
    Swal.fire("Error", err.message, "error");
  }
};

const updateExpense = async () => {
  if (!formData.expenseTypeName) {
    Swal.fire("Oops!", "Expense Type Name is required", "warning");
    return;
  }

  try {
    const fd = new FormData();
    fd.append("ExpenseTypeCode", formData.expenseTypeCode);
    fd.append("ExpenseTypeName", formData.expenseTypeName);
    fd.append("LeadTime", formData.leadTime || "");
    fd.append("IsActive", formData.isActive ? 1 : 0);

    const res = await fetch(`${API_BASE_URL}api/ExpenseType/${selectedExpense.id}`, {
      method: "PUT",
      body: fd, // ✅ FormData
    });

    if (!res.ok) throw new Error("Failed to update expense type");

    setOpenEdit(false);
    setSelectedExpense(null);
    clearForm();
    fetchExpenseTypes();
    Swal.fire("Updated!", "Expense Type updated successfully", "success");
  } catch (err) {
    console.error(err);
    Swal.fire("Error", err.message, "error");
  }
};


  const deleteExpense = async (expense) => {
    const result = await Swal.fire({
      title: `Are you sure?`,
      text: `You are about to delete ${expense.expenseTypeName}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const res = await fetch(
          `${API_BASE_URL}api/ExpenseType/${expense.id}`,
          { method: "DELETE" }
        );
        if (!res.ok) throw new Error("Failed to delete expense type");
        fetchExpenseTypes();
        Swal.fire("Deleted!", "Expense Type has been deleted.", "success");
      } catch (err) {
        console.error(err);
        Swal.fire("Error", err.message, "error");
      }
    }
  };

  // EXPORT
  const exportData = async () => {
    const exportData = expenseTypes.map((e) => ({
      expenseTypeCode: e.expenseTypeCode,
      expenseTypeName: e.expenseTypeName,
      isActive: e.isActive ? 1 : 0,
      leadTime: e.leadTime,
    }));

    const jsonData = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonData], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "expense_types.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  // IMPORT
  const importData = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const jsonData = JSON.parse(text);

      if (jsonData.length > 0) {
        for (const expense of jsonData) {
          await fetch(`${API_BASE_URL}api/ExpenseType`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(expense),
          });
        }
        fetchExpenseTypes();
        Swal.fire("Imported!", "Expense Types imported successfully.", "success");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error!", "Invalid JSON file", "error");
    }
  };

  // ---------------- USE EFFECTS ---------------- //

  useEffect(() => {
    fetchExpenseTypes();
  }, []);

  useEffect(() => {
    if (!search.trim()) {
      setFiltered(expenseTypes);
    } else {
      const term = search.toLowerCase();
      setFiltered(
        expenseTypes.filter(
          (e) =>
            e.expenseTypeName?.toLowerCase().includes(term) ||
            e.expenseTypeCode?.toString().toLowerCase().includes(term)
        )
      );
    }
    setPage(1);
  }, [search, expenseTypes]);

  // ---------------- HELPERS ---------------- //

  const clearForm = () => {
    setFormData({
      expenseTypeCode: "",
      expenseTypeName: "",
      isActive: true,
      leadTime: "",
    });
  };

  const openEditModal = (e) => {
    setSelectedExpense(e);
    setFormData({
      expenseTypeCode: e.expenseTypeCode,
      expenseTypeName: e.expenseTypeName,
      isActive: e.isActive,
      leadTime: e.leadTime,
    });
    setOpenEdit(true);
  };

  // ---------------- RETURN UI ---------------- //
  return (
    <div className="min-h-screen  from-gray-50 to-gray-100 p-8">
      {/* BACK BUTTON */}
      <button
        onClick={() => setCurrentPage("referencess")}
                className="mb-2 flex items-center gap-5 text-indigo-600 hover:text-indigo-800 font-semibold transition-colors group ml-8"
        >
        <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
        Back to References
      </button>

      {/* HEADER */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-3">
              <span className="text-indigo-600 text-4xl font-bold">₱</span>
              Expense Types
            </h1>

            <p className="text-gray-600 mt-2">Manage expense type categories</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportData}
              className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-green-700 text-white px-5 py-3 rounded-xl hover:from-green-700 hover:to-green-800 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
            >
              <Download size={18} /> Export
            </button>

            <label className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-5 py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transition-all transform hover:scale-105 cursor-pointer">
              <Upload size={18} /> Import
              <input
                type="file"
                accept=".json"
                className="hidden"
                onChange={importData}
              />
            </label>

            <button
              onClick={() => setOpenCreate(true)}
              className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-6 py-3 rounded-xl hover:from-indigo-700 hover:to-indigo-800 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
            >
              <Plus size={20} /> Create Expense Type
            </button>
          </div>
        </div>
      </div>

      {/* SEARCH BAR */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by expense type name or code..."
            className="w-full pl-12 pr-4 py-3 bg-white rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all shadow-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* TABLE CARD */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                <th className="px-6 py-4 text-left text-sm font-semibold">Code</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Expense Type</th>
                <th className="px-6 py-4 text-center text-sm font-semibold">Lead Time</th>
                <th className="px-6 py-4 text-center text-sm font-semibold">Status</th>
                <th className="px-6 py-4 text-center text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginated.length > 0 ? (
                paginated.map((expense, index) => (
                  <tr
                    key={expense.id || `expense-${index}`}
                    className="hover:bg-blue-50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                        {expense.expenseTypeCode || "—"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-lg font-bold">
                          ₱
                        </div>

                        <div>
                          <div className="font-semibold text-gray-800">
                            {expense.expenseTypeName}
                          </div>
                          <div className="text-xs text-gray-500">
                            ID: {expense.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center items-center gap-2 text-sm text-gray-700">
                        <Clock size={16} className="text-gray-400" />
                        <span className="font-medium">
                          {expense.leadTime ? `${expense.leadTime} days` : "—"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center">
                        {expense.isActive ? (
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle size={14} />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <XCircle size={14} />
                            Inactive
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => openEditModal(expense)}
                          className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                          title="Edit"
                        >
                          <Edit3 size={16} />
                        </button>
                        <button
                          onClick={() => deleteExpense(expense)}
                          className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-3 text-gray-400">
                      <DollarSign size={48} />
                      <p className="text-lg font-medium">No expense types found</p>
                      <p className="text-sm">
                        Try adjusting your search or create a new expense type
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {paginated.length > 0 && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">
                Showing {(page - 1) * perPage + 1} to{" "}
                {Math.min(page * perPage, filtered.length)} of {filtered.length}{" "}
                expense types
              </span>
              <div className="flex items-center gap-3">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  <ChevronLeft size={18} />
                </button>
                <span className="text-sm font-semibold text-gray-700">
                  Page {page} of {totalPages || 1}
                </span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* MODAL */}
      {(openCreate || openEdit) && (
        <Modal
          title={openCreate ? "Create Expense Type" : "Edit Expense Type"}
          close={() => {
            setOpenCreate(false);
            setOpenEdit(false);
            setSelectedExpense(null);
            clearForm();
          }}
        >
          <div className="space-y-4">
            <Input
              label="Expense Type Code"
              value={formData.expenseTypeCode}
              onChange={(val) =>
                setFormData({ ...formData, expenseTypeCode: val })
              }
            />
            <Input
              label="Expense Type Name*"
              value={formData.expenseTypeName}
              onChange={(val) =>
                setFormData({ ...formData, expenseTypeName: val })
              }
              required
            />
            <Input
              label="Lead Time (days)"
              type="number"
              value={formData.leadTime}
              onChange={(val) => setFormData({ ...formData, leadTime: val })}
            />
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData({ ...formData, isActive: e.target.checked })
                }
                id="isActive"
                className="h-5 w-5 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500"
              />
              <label
                htmlFor="isActive"
                className="text-sm font-semibold text-gray-700 cursor-pointer"
              >
                Active
              </label>
            </div>
          </div>

          <button
            onClick={openCreate ? createExpense : updateExpense}
            className="mt-6 w-full bg-gradient-to-r from-indigo-600 to-indigo-700 text-white py-3 rounded-xl hover:from-indigo-700 hover:to-indigo-800 transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
          >
            {openCreate ? "Create Expense Type" : "Update Expense Type"}
          </button>
        </Modal>
      )}
    </div>
  );
}

// Modal Component
function Modal({ title, close, children }) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={close}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-2 transition"
        >
          <X size={24} />
        </button>
        <h2 className="text-3xl font-bold text-gray-800 mb-8">{title}</h2>
        {children}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required = false }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none"
      />
    </div>
  );
}